import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Pencil, Trash2, Printer, Download, KeyRound, History, UserPlus, UserCog, UserCircle, CalendarOff, Paperclip } from "lucide-react";

const ACTION = {
  created: { label: "Created", icon: PlusCircle, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  updated: { label: "Edited", icon: Pencil, cls: "bg-blue-100 text-blue-800 border-blue-200" },
  deleted: { label: "Deleted", icon: Trash2, cls: "bg-rose-100 text-rose-800 border-rose-200" },
  receipt_printed: { label: "Receipt printed", icon: Printer, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  receipt_downloaded: { label: "Receipt downloaded", icon: Download, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  password_changed: { label: "Password changed", icon: KeyRound, cls: "bg-amber-100 text-amber-800 border-amber-200" },
  user_created: { label: "User created", icon: UserPlus, cls: "bg-purple-100 text-purple-800 border-purple-200" },
  user_updated: { label: "User updated", icon: UserCog, cls: "bg-purple-100 text-purple-800 border-purple-200" },
  password_reset: { label: "Password reset", icon: KeyRound, cls: "bg-amber-100 text-amber-800 border-amber-200" },
  leave_added: { label: "Day off added", icon: CalendarOff, cls: "bg-orange-100 text-orange-800 border-orange-200" },
  leave_removed: { label: "Day off removed", icon: CalendarOff, cls: "bg-orange-100 text-orange-800 border-orange-200" },
  attachment_added: { label: "Report attached", icon: Paperclip, cls: "bg-sky-100 text-sky-800 border-sky-200" },
  attachment_removed: { label: "Report removed", icon: Paperclip, cls: "bg-sky-100 text-sky-800 border-sky-200" },
  profile_updated: { label: "Profile updated", icon: UserCircle, cls: "bg-teal-100 text-teal-800 border-teal-200" },
};

const fmt = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const val = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const Changes = ({ changes }) => (
  <ul className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 space-y-0.5">
    {Object.entries(changes).map(([k, c]) => (
      c && typeof c === "object" && ("from" in c || "to" in c)
        ? <li key={k}><span className="font-mono text-slate-500">{k}</span>: <s className="text-slate-400">{val(c.from)}</s> → <b>{val(c.to)}</b></li>
        : <li key={k}><span className="font-mono text-slate-500">{k}</span>: <b>{val(c)}</b></li>
    ))}
  </ul>
);

export default function AuditLog() {
  const [logs, setLogs] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ actor: "all", action: "all", from: "", to: "" });
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let stale = false;
    const params = {};
    if (filters.actor !== "all") params.actor = filters.actor;
    if (filters.action !== "all") params.action = filters.action;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    api.get("/audit-logs", { params }).then((r) => { if (!stale) setLogs(r.data); }).catch(() => { if (!stale) setLogs([]); });
    return () => { stale = true; };
  }, [filters]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-2xl border border-slate-200 p-4" data-testid="audit-filters">
        <div className="space-y-1"><div className="text-xs text-slate-500">User</div>
          <Select value={filters.actor} onValueChange={set("actor")}>
            <SelectTrigger className="w-56" data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All users</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><div className="text-xs text-slate-500">Action</div>
          <Select value={filters.action} onValueChange={set("action")}>
            <SelectTrigger className="w-52" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All actions</SelectItem>{Object.entries(ACTION).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><div className="text-xs text-slate-500">From</div><Input type="date" value={filters.from} onChange={(e) => set("from")(e.target.value)} className="w-40" data-testid="audit-from-input" /></div>
        <div className="space-y-1"><div className="text-xs text-slate-500">To</div><Input type="date" value={filters.to} onChange={(e) => set("to")(e.target.value)} className="w-40" data-testid="audit-to-input" /></div>
        <button onClick={() => setFilters({ actor: "all", action: "all", from: "", to: "" })} className="text-sm text-teal-700 hover:underline pb-2" data-testid="audit-clear-filters">Clear</button>
      </div>

      {!logs && <div className="text-slate-400 text-sm">Loading history…</div>}
      {logs && !logs.length && <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400" data-testid="audit-empty">No activity matches these filters.</div>}
      {logs && logs.length > 0 && (
        <div className="space-y-3" data-testid="audit-log-list">
          {logs.map((l) => {
            const meta = ACTION[l.action] || { label: l.action, icon: History, cls: "bg-slate-100 text-slate-700" };
            const Icon = meta.icon;
            const changes = l.details?.changes;
            return (
              <div key={l.id} data-testid="audit-log-item" className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-teal-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                    <span className="text-xs text-slate-400 capitalize">{l.entity_type}</span>
                    <span className="font-medium text-slate-900 truncate">{l.label}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">by <b className="text-slate-700">{l.actor_email}</b> · {fmt(l.created_at)}</div>
                  {changes && typeof changes === "object" && !Array.isArray(changes) && Object.keys(changes).length > 0 && <Changes changes={changes} />}
                  {Array.isArray(changes) && changes.length > 0 && <div className="mt-1 text-xs text-slate-500">Fields: {changes.join(", ")}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
